<template>
  <section
    class="message-section custom-section"
    @dragover.prevent="onDragOver"
    @dragenter.prevent="onDragEnter"
    @dragleave="onDragLeave"
    @drop="handleFileDrop"
  >
    <div class="custom-typing">
      {{ typingMessage }}
    </div>

    <div v-if="isLoadingMessages" class="empty-chat">
      <p>{{ t('dialog.chat-message.loading-messages') || 'Loading chat messages, please wait...' }}</p>
    </div>

    <div
      v-else-if="channelUrl && displayedMessages.length > 0"
      id="chatMessages"
      class="content message-list"
      ref="chatListRef"
      @scroll="onScrollMessages"
    >
      <chat-message
        v-for="(msg, idx) in displayedMessages"
        :key="getMessageKey(msg, idx)"
        :message="msg"
        :lastmessage="LastMessage(idx)"
        :id="msg?.messageId || msg?.reqId || idx"
        :is-history="props.isHistory"
        @image-clicked="openImageModal"
      />
    </div>

    <div v-else-if="!isLoadingMessages && channelUrl" class="empty-chat">
      <p>{{ t('dialog.chat-message.no-messages') || 'No messages yet.' }}</p>
    </div>

    <div v-if="isDragging && isChatFileUploadEnabledAsync" class="drop-overlay">
      <p>{{ t('dialog.chat.dropfile') }}</p>
    </div>

    <div class="new-message" v-show="!props.isHistory">
      <div class="message-input-wrap">
        <textarea v-model="message" @keyup="userTyping" ref="messageInput"></textarea>
      </div>

      <div class="message-actions">
        <a
          href="#"
          class="message-action-button"
          @click.prevent="SendMessage"
          :title="t('dialog.button.tooltip.send') || 'Send'"
        >
          <font-awesome-icon icon="paper-plane" />
        </a>

        <button
          type="button"
          class="message-action-button refresh-button-icon"
          :disabled="isRefreshing"
          @click="refreshChat"
          :title="isRefreshing ? (t('dialog.button.tooltip.refreshing') || 'Refreshing...') : (t('dialog.button.tooltip.refresh') || 'Refresh chat')"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M17.65 6.35A7.95 7.95 0 0012 4a8 8 0 100 16 8 8 0 007.75-6h-2.1A6 6 0 1112 6c1.3 0 2.5.42 3.47 1.13L13 10h7V3l-2.35 3.35z"
            />
          </svg>
        </button>

        <button
          v-if="isChatFileUploadEnabledAsync"
          type="button"
          class="message-action-button upload-button-icon"
          @click="triggerFileInput"
          :title="t('dialog.button.tooltip.upload')"
        >
          <font-awesome-icon icon="paperclip" />
        </button>

        <input
          v-if="isChatFileUploadEnabledAsync"
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          style="display: none"
        />
      </div>
    </div>

    <transition name="fade">
      <div v-if="imageModalVisible" class="modal-overlay" @click.self="closeImageModal">
        <button class="modal-close" @click="closeImageModal">×</button>

        <img
          v-if="isImage(selectedImageUrl)"
          :src="selectedImageUrl"
          class="modal-image"
        />

        <video
          v-else
          class="modal-video"
          controls
          autoplay
          :src="selectedImageUrl"
        >
          <source v-if="selectedImageUrl.endsWith('.mp4')" :src="selectedImageUrl" type="video/mp4" />
          <source v-if="selectedImageUrl.endsWith('.webm')" :src="selectedImageUrl" type="video/webm" />
          <source v-if="selectedImageUrl.endsWith('.ogg')" :src="selectedImageUrl" type="video/ogg" />
          {{ t('dialog.chat.video.error') }}
        </video>
      </div>
    </transition>
  </section>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  defineProps,
} from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import ChatMessage from './chat-message.vue'
import { isChatFileUploadEnabled } from '@/plugins/globals'
import { useSendbird } from '@/plugins/sendbird/sendbird-client.js'
import { getI18nInstance } from '@/plugins/i18n'

const getAuthorId = (m) =>
  m?.sender?.userId || m?._sender?.userId || m?.userId || m?.senderId || m?.sender?.user_id || null

const props = defineProps({
  channelUrl: { type: String, required: true },
  channelSet: { type: Object, required: true },
  isHistory: { type: Boolean, default: false }
})

const store = useStore()
const { t } = useI18n()

const {
  getActiveChannel,
  setActiveChannel,
  sendMessage,
  uploadFile,
  openMessageCollection,
  loadPreviousMessages,
  disposeActiveCollection,
  refreshChannelMessages,
} = useSendbird()

const message = ref('')
const typingMessage = ref('')
const waitTimeout = ref(null)
const isDragging = ref(false)
const imageModalVisible = ref(false)
const selectedImageUrl = ref('')
const messageInput = ref(null)
const fileInput = ref(null)
const isChatFileUploadEnabledAsync = ref(false)
const isLoadingMessages = ref(true)
const chatListRef = ref(null)
const isLoadingOlderMessages = ref(false)
const isRefreshing = ref(false)

const refreshThrottleMs = 15000
const lastRefreshAt = ref(0)
let refreshFeedbackTimer = null

const localPreviews = ref([])

const DEBUG_CHAT_DASHBOARD = true

function getDomMessageSnapshot() {
  const el = chatListRef.value || document.getElementById('chatMessages')
  if (!el) {
    return {
      childCount: 0,
      ids: [],
    }
  }

  return {
    childCount: el.childElementCount,
    ids: Array.from(el.children || []).map((child) => ({
      id: child.id,
      text: child.textContent?.trim()?.slice(0, 80),
    })),
  }
}

function logRefreshDashboard(stage, extra = {}) {
  if (!DEBUG_CHAT_DASHBOARD) return

  const dom = getDomMessageSnapshot()
  const active = getActiveChannel()
  const storeMessages = store.getters['sendbird/messages'](props.channelUrl) || []
  const now = Date.now()
  const msSinceLastRefresh = lastRefreshAt.value ? now - lastRefreshAt.value : null

  console.groupCollapsed(`DEBUG => [CHAT DASHBOARD][${stage}] ${props.channelUrl}`)
  console.log({
    requestedChannel: props.channelUrl,
    activeChannel: active?.url || null,
    viewingMatchesActive: props.channelUrl === (active?.url || null),
    isHistory: props.isHistory,
    isRefreshing: isRefreshing.value,
    throttleMs: refreshThrottleMs,
    lastRefreshAt: lastRefreshAt.value || null,
    msSinceLastRefresh,
    canRefreshNow: msSinceLastRefresh === null ? true : msSinceLastRefresh >= refreshThrottleMs,
    storeMessageCount: storeMessages.length,
    previewCount: localPreviews.value.length,
    domChildCount: dom.childCount,
    domIds: dom.ids,
    ...extra,
  })
  console.groupEnd()
}

const normalizeLang = (l) => (l ? l.toLowerCase().split('-')[0] : 'en')
const uiLang = computed(() => {
  try {
    const i18n = getI18nInstance?.()
    const v = i18n?.global?.locale?.value ?? i18n?.global?.locale
    return normalizeLang(v || 'en')
  } catch {
    return 'en'
  }
})

const storeMessages = computed(() => {
  const raw = store.getters['sendbird/messages'](props.channelUrl) || []
  const lang = uiLang.value

  return raw.map(m => {
    const author = getAuthorId(m)
    const withSender = author && !m.sender
      ? { ...m, sender: { userId: author, nickname: m?.senderName || author } }
      : m

    if (withSender?.translations) {
      const base = normalizeLang(lang)
      const localized =
        withSender.translations[lang] ||
        withSender.translations[base]

      if (localized) {
        return { ...withSender, message: localized }
      }
    }

    return withSender
  })
})

const isTyping = computed(() =>
  store.getters['sendbird/isTyping']
)

function getCurrentUserInfo() {
  const chatAs = store.getters['auth/chatAs']
  const profile = store.getters['auth/userProfile']
  const email = chatAs?.email || profile?.email || ''
  const nickname =
    [chatAs?.firstName || profile?.firstName || '', chatAs?.lastName || profile?.lastName || '']
      .filter(Boolean)
      .join(' ')
      .trim() || email || 'Me'

  return { email, nickname }
}

function createLocalPreviewMessage(file) {
  const { email, nickname } = getCurrentUserInfo()
  const previewUrl = URL.createObjectURL(file)
  const tempId = `local-preview-${Date.now()}-${Math.random().toString(36).slice(2)}`

  return {
    __localPreview: true,
    __previewUrl: previewUrl,
    __fileName: file.name,
    __fileType: file.type,
    __createdAt: Date.now(),
    __senderId: email,
    messageId: tempId,
    reqId: tempId,
    requestId: tempId,
    createdAt: Date.now(),
    messageType: 'file',
    customType: 'chat_attachment',
    sender: {
      userId: email,
      nickname,
    },
    name: file.name,
    fileName: file.name,
    filename: file.name,
    type: file.type,
    mimeType: file.type,
    contentType: file.type,
    plainUrl: previewUrl,
    url: previewUrl,
    localPreviewUrl: previewUrl,
    previewUrl,
    file: {
      url: previewUrl,
      localUrl: previewUrl,
      name: file.name,
      type: file.type,
      mimeType: file.type,
      contentType: file.type,
    },
    isFileMessage: () => true,
    isUserMessage: () => false,
    isAdminMessage: () => false,
  }
}

function isSameFileMessage(serverMessage, preview) {
  if (!serverMessage || !preview) return false

  const serverType = String(serverMessage?.messageType || '').toLowerCase()
  if (serverType !== 'file') return false

  const serverSender = (
    serverMessage?.sender?.userId ||
    serverMessage?._sender?.userId ||
    serverMessage?.userId ||
    ''
  ).toLowerCase()

  const previewSender = String(preview.__senderId || '').toLowerCase()
  if (!serverSender || !previewSender || serverSender !== previewSender) return false

  const serverName = String(
    serverMessage?.name ||
    serverMessage?.fileName ||
    serverMessage?.filename ||
    serverMessage?.file?.name ||
    serverMessage?.file?.filename ||
    ''
  ).trim().toLowerCase()

  const previewName = String(preview.__fileName || '').trim().toLowerCase()
  if (!serverName || !previewName || serverName !== previewName) return false

  const serverTs = Number(serverMessage?.createdAt || serverMessage?.created_at || 0)
  const previewTs = Number(preview.__createdAt || 0)

  return Math.abs(serverTs - previewTs) <= 120000
}

const displayedMessages = computed(() => {
  const server = storeMessages.value || []
  const previews = localPreviews.value.filter(preview => {
    return !server.some(serverMsg => isSameFileMessage(serverMsg, preview))
  })

  return [...server, ...previews].sort((a, b) => {
    return Number(a?.createdAt || 0) - Number(b?.createdAt || 0)
  })
})

function getMessageKey(msg, idx) {
  return msg?.messageId || msg?.reqId || msg?.requestId || `${msg?.createdAt ?? 'idx'}-${idx}`
}

function LastMessage(idx) {
  return idx > 0 ? displayedMessages.value[idx - 1] : null
}

function markRefreshedNow() {
  lastRefreshAt.value = Date.now()
}

function showRefreshThrottleFeedback() {
  isRefreshing.value = true

  if (refreshFeedbackTimer) {
    clearTimeout(refreshFeedbackTimer)
  }

  refreshFeedbackTimer = setTimeout(() => {
    isRefreshing.value = false
    refreshFeedbackTimer = null
  }, 300)
}

async function scrollToBottom() {
  await nextTick()
  const el = chatListRef.value || document.getElementById('chatMessages')
  if (!el) return

  await new Promise(requestAnimationFrame)
  el.scrollTop = el.scrollHeight
  await new Promise(requestAnimationFrame)
  el.scrollTop = el.scrollHeight
}

async function openLiveChatCollection() {
  if (props.isHistory) return
  const channel = props.channelSet?.channel
  if (!channel) {
    console.warn('[chat.vue] openLiveChatCollection skipped: no channel in channelSet')
    return
  }

  setActiveChannel(channel)
  await openMessageCollection(channel)
}

async function refreshChat() {
  logRefreshDashboard('REFRESH_CLICKED')

  if (props.isHistory) {
    logRefreshDashboard('REFRESH_SKIPPED_HISTORY')
    return
  }

  const now = Date.now()
  const msSinceLastRefresh = lastRefreshAt.value ? now - lastRefreshAt.value : null
  const refreshAllowed = msSinceLastRefresh === null || msSinceLastRefresh >= refreshThrottleMs

  if (!refreshAllowed) {
    logRefreshDashboard('REFRESH_THROTTLED', {
      msRemaining: refreshThrottleMs - msSinceLastRefresh,
    })
    showRefreshThrottleFeedback()
    return
  }

  if (isRefreshing.value) {
    logRefreshDashboard('REFRESH_SKIPPED_ALREADY_REFRESHING')
    return
  }

  try {
    const beforeStoreMessages = store.getters['sendbird/messages'](props.channelUrl) || []
    const beforeDom = getDomMessageSnapshot()

    isRefreshing.value = true
    markRefreshedNow()

    const active = getActiveChannel() || props.channelSet?.channel
    if (active) {
      setActiveChannel(active)
    }

    logRefreshDashboard('REFRESH_STARTED', {
      beforeStoreMessageCount: beforeStoreMessages.length,
      beforeDomChildCount: beforeDom.childCount,
    })

    const result = await refreshChannelMessages(props.channelUrl, 30)

    await nextTick()
    const afterStoreMessages = store.getters['sendbird/messages'](props.channelUrl) || []
    const afterDom = getDomMessageSnapshot()

    logRefreshDashboard('REFRESH_COMPLETED', {
      serverMessagesReturned: result?.length || 0,
      beforeStoreMessageCount: beforeStoreMessages.length,
      afterStoreMessageCount: afterStoreMessages.length,
      beforeDomChildCount: beforeDom.childCount,
      afterDomChildCount: afterDom.childCount,
      firstServerMessageId: result?.[0]?.messageId || result?.[0]?.reqId || null,
      lastServerMessageId: result?.[result.length - 1]?.messageId || result?.[result.length - 1]?.reqId || null,
    })

    await scrollToBottom()

    logRefreshDashboard('REFRESH_AFTER_SCROLL')
  } catch (err) {
    console.error('[chat.vue] refreshChat failed:', err)
    logRefreshDashboard('REFRESH_FAILED', {
      error: err?.message || String(err),
    })
  } finally {
    isRefreshing.value = false
    logRefreshDashboard('REFRESH_FINALISED')
  }
}

onMounted(async () => {
  if (!props.channelUrl || !props.channelSet) {
    console.warn('[chat.vue] Props not ready — skipping onMounted channelUrl: ', props.channelUrl, ', channelSet: ', props.channelSet)
    isLoadingMessages.value = false
    return
  }

  try {
    isChatFileUploadEnabledAsync.value = await isChatFileUploadEnabled()
    isLoadingMessages.value = true

    if (props.isHistory) {
      await store.dispatch('sendbird/loadFullChatHistory', {
        channelUrl: props.channelUrl,
        channelSet: props.channelSet,
      })
    } else {
      await openLiveChatCollection()

      await store.dispatch('sendbird/loadRecentChatMessages', {
        channelUrl: props.channelUrl,
        channelSet: props.channelSet,
      })
    }
  } catch (error) {
    console.error('Error initializing chat.vue:', error)
  } finally {
    isLoadingMessages.value = false
    await scrollToBottom()
  }
})

onBeforeUnmount(() => {
  if (refreshFeedbackTimer) {
    clearTimeout(refreshFeedbackTimer)
    refreshFeedbackTimer = null
  }

  clearTimeout(waitTimeout.value)

  for (const preview of localPreviews.value) {
    try {
      if (preview?.__previewUrl) URL.revokeObjectURL(preview.__previewUrl)
    } catch {}
  }
  localPreviews.value = []

  if (!props.isHistory) {
    disposeActiveCollection()
  }
})

watch(isTyping, (val) => {
  typingMessage.value = val ? t('dialog.chat.typing') : ''
})

watch(
  () => ({
    len: displayedMessages.value.length,
    url: props.channelUrl,
    loading: isLoadingMessages.value
  }),
  async ({ len, loading }) => {
    if (loading) return
    if (len > 0) {
      await scrollToBottom()
    }
  },
  { immediate: true, deep: false }
)

watch(
  () => displayedMessages.value.length,
  async (len, oldLen) => {
    await nextTick()

    if (!DEBUG_CHAT_DASHBOARD) return

    const dom = getDomMessageSnapshot()
    console.log('DEBUG => [CHAT DASHBOARD][MESSAGE_COUNT_CHANGED]', {
      channel: props.channelUrl,
      oldLen,
      newLen: len,
      domChildCount: dom.childCount,
    })
  }
)

watch(
  () => storeMessages.value,
  () => {
    const server = storeMessages.value || []
    const remaining = []

    for (const preview of localPreviews.value) {
      const matched = server.some(serverMsg => isSameFileMessage(serverMsg, preview))
      if (matched) {
        try {
          if (preview?.__previewUrl) URL.revokeObjectURL(preview.__previewUrl)
        } catch {}
      } else {
        remaining.push(preview)
      }
    }

    localPreviews.value = remaining
  },
  { deep: true }
)

function userTyping(e) {
  clearTimeout(waitTimeout.value)

  const channel = getActiveChannel()
  if (!channel) {
    console.warn('[Sendbird] Cannot type — channel not available yet.')
    return
  }

  channel.startTyping()
  waitTimeout.value = setTimeout(() => {
    channel.endTyping()
  }, 1500)

  if (e.keyCode === 13 && !e.ctrlKey) {
    e.preventDefault()
    SendMessage()
  }
}

async function SendMessage() {
  const msg = message.value.trim()
  if (!msg) return

  message.value = ''

  await nextTick()
  await scrollToBottom()

  try {
    console.log('DEBUG => [SEND MESSAGE]', {
      channel: props.channelUrl,
      message: msg
    })

    await sendMessage(msg)
    await scrollToBottom()
  } catch (e) {
    console.error('[Chat] SendMessage error:', e)

    // Optional: restore message if send fails
    message.value = msg
  }
}

function triggerFileInput() {
  fileInput.value?.click()
}

async function uploadWithLocalPreview(file) {
  const preview = createLocalPreviewMessage(file)
  localPreviews.value = [...localPreviews.value, preview]

  await nextTick()
  await scrollToBottom()

  try {
    await uploadFile(file)

    setTimeout(() => {
      const server = storeMessages.value || []
      const matched = server.some(serverMsg => isSameFileMessage(serverMsg, preview))
      if (matched) {
        try {
          if (preview?.__previewUrl) URL.revokeObjectURL(preview.__previewUrl)
        } catch {}
        localPreviews.value = localPreviews.value.filter(p => p.messageId !== preview.messageId)
      }
    }, 250)
  } catch (err) {
    try {
      if (preview?.__previewUrl) URL.revokeObjectURL(preview.__previewUrl)
    } catch {}
    localPreviews.value = localPreviews.value.filter(p => p.messageId !== preview.messageId)
    throw err
  }
}

async function handleFileSelect(event) {
  const file = event.target.files?.[0]
  if (!file) return

  try {
    await uploadWithLocalPreview(file)
    await scrollToBottom()
  } catch (err) {
    console.error('File upload failed:', err)
  } finally {
    event.target.value = ''
  }
}

async function handleFileDrop(event) {
  if (!isChatFileUploadEnabledAsync.value) return

  event.preventDefault()
  event.stopPropagation()
  isDragging.value = false

  const file = event.dataTransfer?.files?.[0]
  if (file) {
    try {
      await uploadWithLocalPreview(file)
      await scrollToBottom()
    } catch (err) {
      console.error('File upload failed:', err)
    }
  } else {
    console.warn('No file dropped')
  }
}

function onDragEnter() {
  if (isChatFileUploadEnabledAsync.value) {
    isDragging.value = true
  }
}

function onDragOver(event) {
  if (isChatFileUploadEnabledAsync.value) {
    event.preventDefault()
    isDragging.value = true
  }
}

function onDragLeave() {
  if (isChatFileUploadEnabledAsync.value) {
    isDragging.value = false
  }
}

async function onScrollMessages() {
  if (props.isHistory) return
  if (isLoadingMessages.value || isLoadingOlderMessages.value) return

  const el = chatListRef.value
  if (!el) return

  if (el.scrollTop > 0) return

  try {
    isLoadingOlderMessages.value = true

    const beforeHeight = el.scrollHeight

    console.log('DEBUG => [LOAD PREVIOUS] triggered for:', props.channelUrl)
    const older = await loadPreviousMessages(props.channelUrl)

    console.log('DEBUG => [LOAD PREVIOUS RESULT]', {
      count: older?.length || 0
    })
    if (older?.length > 0) {
      await nextTick()
      const afterHeight = el.scrollHeight
      el.scrollTop = afterHeight - beforeHeight
    }
  } catch (err) {
    console.error('[chat.vue] loadPreviousMessages failed:', err)
  } finally {
    isLoadingOlderMessages.value = false
  }
}

function openImageModal(url) {
  selectedImageUrl.value = url
  imageModalVisible.value = true
}

function closeImageModal() {
  imageModalVisible.value = false
  selectedImageUrl.value = ''
}

function isImage(url) {
  return /\.(jpeg|jpg|gif|png|bmp)$/i.test(url)
}
</script>

<style scoped>
.message-section {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  min-height: 400px;
  max-height: 60vh;
  overflow: hidden;
}

.empty-chat {
  flex: 1 1 auto;
  min-height: 0;
  padding: 16px;
  color: #999;
  text-align: center;
  font-style: italic;
  overflow-y: auto;
}

.message-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 22px 8px 8px 8px;
  background: #fff;
}

.new-message {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 8px;
  padding: 10px;
  background: #fff;
  border-top: 1px solid #ccc;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  position: sticky;
  bottom: 0;
  z-index: 5;
}

.message-input-wrap {
  grid-column: 1;
  width: 100%;
  min-width: 0;
  align-self: stretch;
  justify-self: stretch;
}

.new-message textarea {
  display: block !important;
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  min-height: 56px;
  max-height: 120px;
  resize: vertical;
  padding: 8px 10px;
  font-size: 14px;
  line-height: 1.4;
  border-radius: 5px;
  border: 1px solid #ccc;
  overflow-y: auto;
  box-sizing: border-box;
}

.message-actions {
  grid-column: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.message-action-button {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  color: #007bff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  box-sizing: border-box;
}

.refresh-button-icon:disabled svg {
  animation: spin 0.9s linear infinite;
}

.message-action-button:hover {
  color: #0056b3;
}

.message-action-button:disabled,
.refresh-button-icon:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.custom-typing {
  position: absolute;
  top: 4px;
  left: 0;
  width: 100%;
  text-align: center;
  font-size: 0.7em;
  font-weight: 500;
  z-index: 10;
  pointer-events: none;
}

.drop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 123, 255, 0.1);
  border: 2px dashed #007bff;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007bff;
  font-size: 1.2em;
  pointer-events: none;
}

button {
  background: none;
  border: none;
  color: blue;
  text-decoration: underline;
  cursor: pointer;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-image,
.modal-video {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 30px;
  font-size: 2rem;
  color: #fff;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 10000;
}

.modal-close:hover {
  color: #ccc;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
